import { jsx } from 'react/jsx-runtime';
import { css, styled } from 'styled-components';
import { mediaHover, mediaNotMobile, mediaMobile } from '../constants/styled-utils.js';
import { preventForwardPropsConfig } from '../utils/preventForwardProps.js';
import { cssVar } from '../theme/variables.js';
import { ForcedTheme } from '../theme/ForcedTheme.js';
import { Button } from './Button.js';
import { motion } from 'framer-motion';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { useResponsive } from '../hooks/useResponsive.js';
import { ScrollArea } from './ScrollArea.js';
import { Flex } from './Flex.js';

const floatingCss = css([
  `display:flex;gap:6px;padding:6px;align-items:center;z-index:10;height:52px;border-radius:14px;outline:1px solid rgba(`,
  `,0.05);background:`,
  `;box-shadow:0 14px 24px 0 rgb(128 128 128 / 24%),0 2px 4px 0 rgb(128 128 128 / 22%);`,
  `{&:hover{scale:1.02;}}`,
  ` transition:z-index 0.3s step-end,scale 0.2s;`
], cssVar["color-bg-primary-reverse-rgb"], cssVar["color-bg-primary"], mediaHover, (p) => p.highlighted && css([
  `background-color:`,
  `;`
], cssVar["color-app-primary"]));
const ContainerBase = styled(motion.div).attrs((p) => {
  return {
    layoutId: "dashboard-footer",
    layout: true,
    initial: {
      opacity: 0,
      y: "200%"
    },
    exit: {
      opacity: 0,
      y: "200%"
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.15
    },
    ...p
  };
}).withConfig(preventForwardPropsConfig([
  "alwaysFloating",
  "highlighted"
])).withConfig({
  displayName: "FloatingFooter__ContainerBase",
  componentId: "sc-99aaa31d-0"
})([
  ``,
  ``
], (p) => p.alwaysFloating ? floatingCss : css([
  ``,
  `{`,
  `;}`,
  `{display:flex;justify-content:center;gap:6px;padding-bottom:var(--safe-dashboard-bottom-offset);width:100%;background-color:`,
  `;}`
], mediaNotMobile, floatingCss, mediaMobile, cssVar["color-bg-primary"]));
const ContainerWrapper = styled.div.withConfig({
  displayName: "FloatingFooter__ContainerWrapper",
  componentId: "sc-99aaa31d-1"
})([
  `position:fixed;bottom:calc( 12px + var(--safe-dashboard-bottom-offset) + `,
  `px );left:50%;transform:translateX(-50%);z-index:15;--scrollbar-fade-width:2rem;`,
  ``
], (p) => p.additionalBottomOffset || 0, (p) => !p.alwaysFloating && css([
  ``,
  `{bottom:0;left:0;transform:none;width:100%;}`
], mediaMobile));
const Container = ({ additionalBottomOffset, className, setRef, alwaysFloating, children, ...props }) => {
  const { isMobileView } = useResponsive();
  useClientLayoutEffect(() => {
    if (isMobileView) return;
    document.documentElement.style.setProperty("--safe-margin-offset-bottom", `calc(env(safe-area-inset-bottom) + 80px)`);
    return () => document.documentElement.style.setProperty("--safe-margin-offset-bottom", "env(safe-area-inset-bottom)");
  }, [
    isMobileView
  ]);
  const renderScroll = isMobileView && !alwaysFloating;
  return /* @__PURE__ */ jsx(ForcedTheme, {
    colorScheme: "invert",
    asChild: true,
    children: /* @__PURE__ */ jsx(ContainerWrapper, {
      alwaysFloating,
      additionalBottomOffset,
      className,
      children: /* @__PURE__ */ jsx(ContainerBase, {
        alwaysFloating,
        ...props,
        ref: setRef,
        children: renderScroll ? /* @__PURE__ */ jsx(ScrollArea.Default, {
          fadeColor: "color-bg-primary",
          orientation: "horizontal",
          scrollbarVariant: "none",
          fade: "both",
          background: "primary",
          style: {
            width: "100%"
          },
          children: /* @__PURE__ */ jsx(Flex, {
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            style: {
              padding: "8px"
            },
            children
          })
        }) : children
      })
    })
  });
};
const footerButtonMotionProps = {
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  },
  transition: {
    duration: 0.3,
    delay: 0.1
  },
  initial: {
    opacity: 0
  }
};
const FooterButtonItem = styled(motion(Button)).attrs((p) => {
  return {
    variant: "transparent",
    layout: "preserve-aspect",
    ...footerButtonMotionProps,
    ...p
  };
}).withConfig({
  displayName: "FloatingFooter__FooterButtonItem",
  componentId: "sc-99aaa31d-2"
})([
  `border:none !important;color:`,
  `;& [data-fabric-icon],& svg{width:24px;height:24px;}`,
  `{&:hover{background-color:rgba(`,
  `,0.13) !important;}}`,
  ` `,
  ` `,
  ` transition:0.2s background-color,0.2s scale;&:active{scale:0.85;}`
], cssVar["color-text-primary"], mediaHover, cssVar["color-bg-primary-reverse-rgb"], (p) => p.active && css([
  `background-color:rgba(`,
  `,0.13) !important;`
], cssVar["color-bg-primary-reverse-rgb"]), (p) => p.variant === "transparent" && css([
  `&:disabled{background:none !important;box-shadow:none !important;}`
]), (p) => p.variant === "danger" && css([
  `background-color:rgba(255,0,0,0.3) !important;color:#ff0000;kbd{color:#ff0000;}`,
  `{&:hover{background-color:rgba(255,0,0,0.3) !important;}}&:disabled{color:rgba(255,255,255,0.3) !important;}`
], mediaHover));
const FloatingFooter = {
  Container,
  Button: FooterButtonItem,
  footerButtonMotionProps
};

export { FloatingFooter };
//# sourceMappingURL=FloatingFooter.js.map
